import * as React from 'react'
import { useEffect } from 'react'
import { useLocation } from 'react-use'
import { Field } from 'redux-form'
import { Link } from 'react-router'

import { dispatchToProps, IBillingBillsListContainerProps } from './BillingBillsList.container'
import { Table, TableHeader, TableRow } from 'features/Table'
import Pagination from 'components/Pagination'
import EmptyMessageCard from 'components/Cards/EmptyMessageCard'
import { useGetBillingBillsQuery } from 'src/generated/graphql'
import LoadingOverlay from 'components/Loading/LoadingOverlay'
import SelectPretty from 'components/Input/SelectPretty'
import { Row, Col } from 'components/Layout'
import Button from 'components/Button'
import Popover from 'components/Popover/Popover'
import { getBillDocumentUrl } from 'features/Billing/Billing.utils'

import cn from 'classnames'
import * as sPopover from 'components/Popover/Popover.scss'
import * as sLay from 'styles/modules/layout.scss'
import * as sMarg from 'styles/modules/margins.scss'

export interface IBillingBillsListComponentProps extends IBillingBillsListContainerProps {
  replace: typeof dispatchToProps.replace
  banOptions: any

  showPayWithCreditCardFormModal: Function
  showCasesCreateBillingDisputeModal: Function
  showCasesCreateEnquiryModal: Function
}

export const BillingBillsListComponent = (props: IBillingBillsListComponentProps) => {
  const { hash } = useLocation()

  const { showCasesCreateBillingDisputeModal, showCasesCreateEnquiryModal } = props

  useEffect(() => {
    switch (hash) {
      case '#__dispute':
        showCasesCreateBillingDisputeModal()
        break
      case '#__enquiry':
        showCasesCreateEnquiryModal()
        break
    }
  }, [])

  const { page, replace, banOptions, ban, showPayWithCreditCardFormModal } = props

  const selectBan = ban => {
    replace({
      pathname: '/dashboard/billing',
      query: { ban, page: 1 },
    })
  }

  const { data, loading, refetch } = useGetBillingBillsQuery({
    variables: { page, ban },
    fetchPolicy: 'no-cache',
  })

  if (loading || !data) return <LoadingOverlay transparent />
  const { results, totalPages } = data.billingBills

  return (
    <div>
      <Row
        className={cn(
          sMarg.mb30,
          sLay['layout-gt-xs-column'],
          sLay['layout-gt-xs-row'],
          sLay['layout-align-end'],
          'sm'
        )}
      >
        <Col className={cn('sm', sMarg.mb15xs)}>
          <Field
            label="Billing Account"
            component={SelectPretty}
            name="ban"
            options={banOptions}
            display="box"
            onChangeAlt={selectBan}
          />
        </Col>
        <Button
          style={{ marginLeft: '10px' }}
          color="tertiary"
          size="sm"
          rect
          onClick={() => showCasesCreateEnquiryModal()}
        >
          Bill Enquiry
        </Button>
        <Button
          style={{ marginLeft: '10px' }}
          color="tertiary"
          size="sm"
          rect
          onClick={() => showCasesCreateBillingDisputeModal()}
        >
          Dispute a Bill
        </Button>
      </Row>
      {results.length === 0 ? (
        <EmptyMessageCard
          title="You have no bills yet."
          subtitle="They'll appear here at the end of your next billing cycle."
        />
      ) : (
        <div>
          <Table
            header={
              <TableHeader>
                <div className={sLay['hide-xs']}>Bill Number</div>
                <div className={sLay['hide-xs']}>Billing Account</div>
                <div>Due Date</div>
                <div>Total</div>
                <div style={{ maxWidth: '76px' }}>Status</div>
                <div style={{ maxWidth: '76px' }} className={sLay['hide-xs']} />
              </TableHeader>
            }
          >
            {results.map(bill => {
              return (
                <TableRow key={bill.id}>
                  <div className={sLay['hide-xs']}>{bill.billNumber}</div>
                  <div className={sLay['hide-xs']}>
                    <Link to={`/dashboard/billing/accounts/${bill.ban}`}>{bill.ban}</Link>
                  </div>
                  <div>{bill.dueDate}</div>
                  <div>${bill.total}</div>
                  <div style={{ maxWidth: '76px' }}>
                    {bill.status === 'PAID' ? (
                      <Button color="tertiary" size="xs" disabled>
                        Paid
                      </Button>
                    ) : (
                      <Button
                        color="tertiary"
                        onClick={() =>
                          showPayWithCreditCardFormModal({
                            bill,
                            afterPay: refetch,
                          })
                        }
                        size="xs"
                      >
                        Pay
                      </Button>
                    )}
                  </div>
                  <div style={{ maxWidth: '76px' }} className={sLay['hide-xs']}>
                    {bill.documents.length > 1 && (
                      <Popover preferPlace="below" trigger="click">
                        <Button color="tertiary" size="xs" ghost>
                          View
                        </Button>
                        <div className={sPopover.popup}>
                          {bill.documents.map((document, idx) => (
                            <a
                              key={idx}
                              href={getBillDocumentUrl({
                                billId: bill.id,
                                fileName: document.fileName,
                              })}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {document.fileName}
                            </a>
                          ))}
                        </div>
                      </Popover>
                    )}
                    {bill.documents.length === 1 && (
                      <Button
                        color="tertiary"
                        size="xs"
                        ghost
                        href={getBillDocumentUrl({
                          billId: bill.id,
                          fileName: bill.documents[0].fileName,
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </Button>
                    )}
                  </div>
                </TableRow>
              )
            })}
          </Table>
        </div>
      )}
      <Pagination
        page={page}
        numPages={totalPages}
        onClick={nextPage =>
          replace({
            pathname: location.pathname,
            query: {
              ban,
              page: nextPage,
            },
          })
        }
      />
    </div>
  )
}



// WEBPACK FOOTER //
// src/features/BillingBills/BillingBillsList/BillingBillsList.tsx